import React, { useState, useEffect } from "react";
import { Typography, Box, Snackbar, Alert } from "@mui/material";
import { LoadingState } from "../../States";
import { Button, TextField, Container } from "@/components/atoms";
import { isValidPassword } from "@/utils/validators";
import { useResetPassword } from "@/hooks";

export const ResetPassword = () => {
  /* --------------------- Hooks --------------------- */

  const { data, loading, error, resetPassword } = useResetPassword();

  /* --------------------- State --------------------- */

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordConfirmationError, setPasswordConfirmationError] = useState<
    string | null
  >(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const [snackBar, setSnackBar] = useState({
    state: false,
    msg: "",
    severity: "success" as "success" | "error",
  });

  /* --------------------- Constants --------------------- */
  const queryParams = new URLSearchParams(window.location.search);
  const token = queryParams.get("token");
  const email = queryParams.get("email");

  /* --------------------- Lifecycle Methods --------------------- */

  useEffect(() => {
    setPasswordError(
      password && !isValidPassword(password)
        ? "Password must be at least 8 characters."
        : null,
    );
    setPasswordConfirmationError(
      password && passwordConfirmation && password !== passwordConfirmation
        ? "Passwords do not match."
        : null,
    );
  }, [password, passwordConfirmation]);

  useEffect(() => {
    setIsFormValid(
      !passwordError &&
        !passwordConfirmationError &&
        password !== "" &&
        passwordConfirmation !== "",
    );
  }, [passwordError, passwordConfirmationError, password]);

  useEffect(() => {
    if (data?.success) {
      setSnackBar({
        state: true,
        msg: "Password reset successful. Redirecting to login...",
        severity: "success",
      });

      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setSnackBar({
        state: true,
        msg: error,
        severity: "error",
      });
    }
  }, [error]);

  /* --------------------- Handles --------------------- */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid || !email || !token) {
      setSnackBar({
        state: true,
        msg: "Missing or invalid email/token.",
        severity: "error",
      });
      return;
    }
    await resetPassword({
      email,
      token,
      new_password: password,
      confirm_password: passwordConfirmation,
    });
  };

  return (
    <Container
      componentName="reset-password"
      sx={{
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Snackbar
        open={snackBar.state}
        autoHideDuration={6000}
        onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackBar.severity}
          variant="filled"
          onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        >
          {snackBar.msg}
        </Alert>
      </Snackbar>

      {loading ? (
        <LoadingState message="Resetting your password..." />
      ) : (
        <>
          <Box justifyContent="center" display="flex" sx={{ margin: 2 }}>
            <Typography variant="h4" gutterBottom>
              Reset Password
            </Typography>
          </Box>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              width: "100%",
              maxWidth: "400px",
              border: "none",
            }}
          >
            <TextField
              componentName="reset-password"
              type="password"
              variant="outlined"
              placeholder="New Password"
              value={password}
              label="New Password"
              onChange={e => setPassword(e.target.value)}
              required
              error={!!passwordError}
              helperText={passwordError}
              fullWidth
            />
            <TextField
              componentName="reset-password-confirmation"
              type="password"
              variant="outlined"
              placeholder="Confirm Password"
              value={passwordConfirmation}
              label="Confirm Password"
              onChange={e => setPasswordConfirmation(e.target.value)}
              required
              error={!!passwordConfirmationError}
              helperText={passwordConfirmationError}
              fullWidth
            />
            <Button
              color="primary"
              type="submit"
              disabled={!isFormValid || loading}
              componentName="reset-password-button"
            >
              Reset Password
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
};
